import { useCallback, useRef, useState } from 'react';
import { FileUp, Loader2, X, AlertTriangle, Check } from 'lucide-react';
import {
  parseLotesExcelArquivo,
  type LinhaLoteExcelPreview,
} from '../../utils/importarLotesExcel';

export interface LoteImportadoExcel {
  lote: string;
  produtoNome: string;
  qtd: number;
  corProduto?: string;
  codigoProduto?: string;
  dataHoraInicioCorte?: string;
  dataHoraFimEmbalagem?: string;
}

interface Props {
  nomesPontuacao: readonly string[];
  onImportar: (itens: LoteImportadoExcel[]) => void;
  onFechar: () => void;
}

export default function ImportarLotesExcelModal({ nomesPontuacao, onImportar, onFechar }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [avisos, setAvisos] = useState<string[]>([]);
  const [linhas, setLinhas] = useState<LinhaLoteExcelPreview[]>([]);
  const [nomeArquivo, setNomeArquivo] = useState('');

  const processarArquivo = useCallback(
    async (file: File) => {
      setCarregando(true);
      setErro('');
      setAvisos([]);
      setLinhas([]);
      setNomeArquivo(file.name);
      try {
        const r = await parseLotesExcelArquivo(file, nomesPontuacao);
        setLinhas(r.linhas);
        setAvisos(r.avisos);
        if (r.linhas.length === 0) {
          setErro('Nenhum lote reconhecido nesta planilha.');
        }
      } catch (e) {
        setErro(e instanceof Error ? e.message : String(e));
      } finally {
        setCarregando(false);
      }
    },
    [nomesPontuacao]
  );

  const toggleLinha = (id: string) => {
    setLinhas((prev) => prev.map((l) => (l.id === id ? { ...l, selecionada: !l.selecionada } : l)));
  };

  const toggleTodas = (valor: boolean) => {
    setLinhas((prev) => prev.map((l) => ({ ...l, selecionada: valor })));
  };

  const atualizarCampo = (id: string, patch: Partial<LinhaLoteExcelPreview>) => {
    setLinhas((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const confirmar = () => {
    const sel = linhas.filter((l) => l.selecionada && l.lote && l.produtoNome && l.qtd > 0);
    if (sel.length === 0) {
      alert('Selecione ao menos uma linha válida para importar.');
      return;
    }
    onImportar(
      sel.map((l) => ({
        lote: l.lote,
        produtoNome: l.produtoNome,
        qtd: l.qtd,
        corProduto: l.corProduto,
        codigoProduto: l.codigoProduto,
        dataHoraInicioCorte: l.dataHoraInicioCorte,
        dataHoraFimEmbalagem: l.dataHoraFimEmbalagem,
      }))
    );
    onFechar();
  };

  const selecionadas = linhas.filter((l) => l.selecionada).length;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
      <div
        className="bg-[#fafbfc] rounded-2xl shadow-xl border border-[#009b3a]/20 w-full max-w-6xl max-h-[90vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="importar-lotes-excel-titulo"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#009b3a]/15">
          <div>
            <h2 id="importar-lotes-excel-titulo" className="text-lg font-semibold text-black">
              Importar lotes do Excel
            </h2>
            <p className="text-xs text-neutral-600 mt-0.5">
              Planilha do Programa de Produção: Produto, N°Lote, Qtd, Corte (início) e Embalagem (fim).
              Cor e código vêm do nome do produto.
            </p>
          </div>
          <button type="button" onClick={onFechar} className="p-2 rounded-lg hover:bg-neutral-100" title="Fechar">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-[#009b3a]/10 flex flex-wrap gap-2 items-center">
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void processarArquivo(f);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={carregando}
            className="px-4 py-2 rounded-lg border border-[#009b3a]/40 bg-[#e8f5ec] text-[#006b28] text-sm font-semibold flex items-center gap-2 hover:bg-[#d4edda] disabled:opacity-50"
          >
            {carregando ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
            {carregando ? 'Lendo planilha…' : 'Escolher Excel'}
          </button>
          {nomeArquivo && <span className="text-xs text-neutral-600 truncate max-w-xs">{nomeArquivo}</span>}
          {linhas.length > 0 && (
            <>
              <button type="button" onClick={() => toggleTodas(true)} className="text-xs px-2 py-1 rounded border border-neutral-300">
                Marcar todas
              </button>
              <button type="button" onClick={() => toggleTodas(false)} className="text-xs px-2 py-1 rounded border border-neutral-300">
                Desmarcar todas
              </button>
              <span className="text-xs text-neutral-700 ml-auto">
                {selecionadas} de {linhas.length} selecionada(s)
              </span>
            </>
          )}
        </div>

        {erro && (
          <p className="mx-5 mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
            {erro}
          </p>
        )}
        {avisos.length > 0 && (
          <ul className="mx-5 mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-0.5">
            {avisos.map((a, i) => (
              <li key={i}>• {a}</li>
            ))}
          </ul>
        )}

        <div className="flex-1 overflow-auto px-5 py-3">
          {linhas.length > 0 ? (
            <table className="w-full text-xs border-collapse min-w-[900px]">
              <thead className="bg-[#e4e6ec] sticky top-0">
                <tr>
                  <th className="px-2 py-2 border border-[#009b3a]/15 w-8" />
                  <th className="px-2 py-2 border border-[#009b3a]/15 text-left">Lote</th>
                  <th className="px-2 py-2 border border-[#009b3a]/15 text-left">Produto (pontuação)</th>
                  <th className="px-2 py-2 border border-[#009b3a]/15 text-left">Cor</th>
                  <th className="px-2 py-2 border border-[#009b3a]/15 text-left">Cód.</th>
                  <th className="px-2 py-2 border border-[#009b3a]/15 text-right">Qtd</th>
                  <th className="px-2 py-2 border border-[#009b3a]/15 text-left">Início corte</th>
                  <th className="px-2 py-2 border border-[#009b3a]/15 text-left">Fim emb.</th>
                  <th className="px-2 py-2 border border-[#009b3a]/15 text-left">Nome original</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l) => (
                  <tr
                    key={l.id}
                    className={`odd:bg-white even:bg-[#f8f9fa] ${!l.encontradoCatalogo ? 'bg-amber-50/60' : ''}`}
                  >
                    <td className="px-2 py-1 border border-[#009b3a]/10 text-center">
                      <input type="checkbox" checked={l.selecionada} onChange={() => toggleLinha(l.id)} className="w-4 h-4" />
                    </td>
                    <td className="px-1 py-1 border border-[#009b3a]/10">
                      <input
                        value={l.lote}
                        onChange={(e) => atualizarCampo(l.id, { lote: e.target.value })}
                        className="w-full px-1 py-0.5 border border-neutral-200 rounded text-xs"
                      />
                    </td>
                    <td className="px-1 py-1 border border-[#009b3a]/10">
                      <input
                        value={l.produtoNome}
                        onChange={(e) =>
                          atualizarCampo(l.id, {
                            produtoNome: e.target.value,
                            encontradoCatalogo: nomesPontuacao.some(
                              (n) => n.toLowerCase() === e.target.value.toLowerCase()
                            ),
                          })
                        }
                        className="w-full px-1 py-0.5 border border-neutral-200 rounded text-xs"
                        title={l.aviso}
                      />
                    </td>
                    <td className="px-1 py-1 border border-[#009b3a]/10">
                      <input
                        value={l.corProduto || ''}
                        onChange={(e) => atualizarCampo(l.id, { corProduto: e.target.value || undefined })}
                        className="w-full px-1 py-0.5 border border-neutral-200 rounded text-xs"
                        placeholder="—"
                      />
                    </td>
                    <td className="px-1 py-1 border border-[#009b3a]/10">
                      <input
                        value={l.codigoProduto || ''}
                        onChange={(e) => atualizarCampo(l.id, { codigoProduto: e.target.value || undefined })}
                        className="w-full px-1 py-0.5 border border-neutral-200 rounded text-xs"
                        placeholder="—"
                      />
                    </td>
                    <td className="px-1 py-1 border border-[#009b3a]/10">
                      <input
                        type="number"
                        value={l.qtd || ''}
                        min={0}
                        onChange={(e) => atualizarCampo(l.id, { qtd: Number(e.target.value) || 0 })}
                        className="w-16 px-1 py-0.5 border border-neutral-200 rounded text-xs text-right"
                      />
                    </td>
                    <td className="px-1 py-1 border border-[#009b3a]/10">
                      <input
                        type="datetime-local"
                        value={l.dataHoraInicioCorte || ''}
                        onChange={(e) => atualizarCampo(l.id, { dataHoraInicioCorte: e.target.value || undefined })}
                        className="w-full px-0.5 py-0.5 border border-neutral-200 rounded text-[10px]"
                      />
                    </td>
                    <td className="px-1 py-1 border border-[#009b3a]/10">
                      <input
                        type="datetime-local"
                        value={l.dataHoraFimEmbalagem || ''}
                        onChange={(e) => atualizarCampo(l.id, { dataHoraFimEmbalagem: e.target.value || undefined })}
                        className="w-full px-0.5 py-0.5 border border-neutral-200 rounded text-[10px]"
                      />
                    </td>
                    <td
                      className="px-2 py-1 border border-[#009b3a]/10 text-neutral-500 max-w-[140px] truncate"
                      title={l.textoProdutoOriginal}
                    >
                      {l.textoProdutoOriginal || '—'}
                      {!l.encontradoCatalogo && (
                        <span className="ml-1 inline-flex items-center text-amber-700" title="Ajuste o nome depois">
                          <AlertTriangle className="w-3 h-3" />
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            !carregando && (
              <p className="text-center text-neutral-500 py-12 text-sm">
                Escolha o arquivo Excel (.xlsx) exportado do Programa de Produção.
              </p>
            )
          )}
        </div>

        <div className="px-5 py-4 border-t border-[#009b3a]/15 flex justify-end gap-2">
          <button type="button" onClick={onFechar} className="px-4 py-2 rounded-lg border border-neutral-300 text-sm font-medium">
            Cancelar
          </button>
          <button
            type="button"
            onClick={confirmar}
            disabled={linhas.length === 0 || selecionadas === 0}
            className="px-4 py-2 rounded-lg ade-btn-gold relative z-10 text-sm font-semibold flex items-center gap-2 disabled:opacity-40"
          >
            <Check className="w-4 h-4" />
            Importar {selecionadas > 0 ? `(${selecionadas})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
